import { embedEditorFont } from './editorFonts';
// src/lib/appearanceStream.js
//
// Builds /AP /N Form XObjects for FreeText annotations using hand-written PDF
// text operators. Viewers that honor /AP (Preview, Acrobat, Chrome) paint this
// verbatim instead of auto-redrawing from /DA + /Contents — the mechanism that
// makes SmallPDF's output render identically everywhere.
//
// Worker-safe: pure pdf-lib, no DOM.

import { PDFName, PDFNumber } from 'pdf-lib';
import { editToSegments } from './richText';

// Helvetica/Noto descent is ~0.21em; reserve 0.25·fontSize so descenders
// (g, j, p, q, y) stay inside the BBox and the value sits ON the writing
// line for tight field rects instead of floating above it.
const DESCENDER_RATIO = 0.25;

/** Escape a string for a PDF literal-string (...) operand. */
export function escapePdfText(s) {
  return String(s).replace(/\\/g, '\\\\').replace(/\(/g, '\\(').replace(/\)/g, '\\)');
}

/**
 * Assemble the appearance content stream. Each line is its own BT/ET block so
 * the Td offset is absolute (a single BT with successive Td's would accumulate
 * — line 2 would land relative to line 1, not the box origin).
 */
function buildAbsoluteStream({ lines, font, fontName, fontSize, color, width, align, firstBaseline, lineHeight }) {
  const [r, g, b] = color.map(fmtC);
  const parts = ['/Tx BMC', 'q'];
  lines.forEach((ln, i) => {
    const w = font.widthOfTextAtSize(ln, fontSize);
    let x = 2;
    if (align === 'center') x = Math.max(2, (width - w) / 2);
    else if (align === 'right') x = Math.max(2, width - w - 2);
    const y = firstBaseline - i * lineHeight;
    // Encode with the font so embedded/subset fonts get correct glyph bytes.
    // encodeText returns a PDFHexString → renders as <hex> in the stream.
    const encoded = font.encodeText(ln);
    parts.push('BT', `/${fontName} ${fontSize} Tf`, `${r} ${g} ${b} rg`, `${x.toFixed(2)} ${y.toFixed(2)} Td ${encoded} Tj`, 'ET');
  });
  parts.push('Q', 'EMC');
  return parts.join('\n');
}

/**
 * @param {import('pdf-lib').PDFDocument} doc
 * @param {{
 *   text:string, font:import('pdf-lib').PDFFont, fontSize:number,
 *   color:[number,number,number], width:number, height:number,
 *   align?:'left'|'center'|'right'
 * }} opts
 * @returns {{ xobjectRef: import('pdf-lib').PDFRef, fontResourceName: string }}
 */
export function buildTextAppearance(doc, { text, font, fontSize, color, width, height, align = 'left' }) {
  const fontName = 'Helv';
  const lines = String(text).split(/\r\n|\r|\n/);
  const lineHeight = fontSize * 1.2;
  // First (top) line baseline: one font-size below the top, with a 2pt pad,
  // but never below the descender pad — that clamp keeps glyph descenders
  // inside the BBox and biases tight single-line field rects (height ≈
  // fontSize + 2, writing line at the bottom) onto the line instead of
  // floating a value ~fontSize above it. Taller text boxes are unaffected.
  const descenderPad = fontSize * DESCENDER_RATIO;
  const firstBaseline = Math.max(descenderPad, height - 2 - fontSize);
  const stream = buildAbsoluteStream({ lines, font, fontName, fontSize, color, width, align, firstBaseline, lineHeight });
  const xobjDict = {
    Type: PDFName.of('XObject'), Subtype: PDFName.of('Form'), FormType: PDFNumber.of(1),
    BBox: doc.context.obj([PDFNumber.of(0), PDFNumber.of(0), PDFNumber.of(width), PDFNumber.of(height)]),
    Resources: doc.context.obj({ Font: doc.context.obj({ [fontName]: font.ref }) }),
  };
  const xobjectRef = doc.context.register(doc.context.flateStream(stream, xobjDict));
  return { xobjectRef, fontResourceName: fontName };
}

// Editable text boxes have per-character styles. The plain form-field
// appearance above deliberately stays simple; using it for the editor used
// to discard bold, italic, font families, colors and line spacing on save.
export async function buildEditTextAppearance(doc, edit, cache) {
  const width = Math.max(1, Math.abs(edit.width));
  let height = Math.max(1, Math.abs(edit.height));
  const fonts = new Map();
  const tokens = [];
  for (const segment of editToSegments(edit)) {
    const styles = segment.styles || {};
    const family = styles.fontFamily || 'Helvetica';
    const base = /Courier/.test(family) ? 'Courier' : /Times|Roboto Slab/.test(family) ? 'Times' : 'Helvetica';
    const key = base === 'Times'
      ? `Times-${styles.bold && styles.italic ? 'BoldItalic' : styles.bold ? 'Bold' : styles.italic ? 'Italic' : 'Roman'}`
      : base + (styles.bold && styles.italic ? '-BoldOblique' : styles.bold ? '-Bold' : styles.italic ? '-Oblique' : '');
    cache.styled ||= {};
    const font = edit.textLayoutVersion === 2 ? await embedEditorFont(doc, styles, cache) : hasNonWinAnsi(segment.text)
      ? await resolveAppearanceFont(doc, segment.text, cache)
      : (cache.styled[key] ||= key === 'Helvetica' ? cache.helv : await doc.embedFont(key));
    if (edit.textLayoutVersion === 2) {
      const supported = new Set(font.getCharacterSet());
      for (const ch of segment.text) {
        if (!/\s/.test(ch) && !supported.has(ch.codePointAt(0))) throw new Error(`The selected font cannot render “${ch}”. Choose another font before saving.`);
      }
    }
    if (!fonts.has(font)) fonts.set(font, `F${fonts.size}`);
    for (const token of segment.text.replace(/\r\n?/g, '\n').replace(/\t/g, '    ').split(/(\n| +)/).filter(Boolean)) {
      tokens.push({ text: token, styles, font, size: Number(styles.fontSize) || 14 });
    }
  }
  const lines = [{ runs: [], width: 0, size: 0 }];
  for (const token of tokens) {
    if (token.text === '\n') { lines.push({ runs: [], width: 0, size: token.size }); continue; }
    const tokenWidth = token.font.widthOfTextAtSize(token.text, token.size);
    let line = lines.at(-1);
    if (line.runs.length && line.width + tokenWidth > width && token.text.trim()) {
      line = { runs: [], width: 0, size: token.size }; lines.push(line);
    }
    // Very long words follow the editor's overflow-wrap:break-word behavior.
    for (const part of tokenWidth > width ? Array.from(token.text) : [token.text]) {
      const partWidth = token.font.widthOfTextAtSize(part, token.size);
      if (line.width && line.width + partWidth > width && part.trim()) {
        line = { runs: [], width: 0, size: token.size }; lines.push(line);
      }
      line.runs.push({ ...token, text: part, width: partWidth });
      line.width += partWidth; line.size = Math.max(line.size, token.size);
    }
  }
  const leading = edit.styles?.lineHeight || 1.3;
  const defaultSize = Number(editToSegments(edit)[0]?.styles?.fontSize) || 14;
  for (const line of lines) line.size = Math.max(line.size, defaultSize);
  const requiredHeight = lines.reduce((sum, line) => sum + line.size * leading, 0);
  height = Math.max(height, requiredHeight);
  const rgb = (hex = '#000000') => {
    const expanded = hex.length === 4 ? `#${[...hex.slice(1)].map((c) => c + c).join('')}` : hex;
    return [1, 3, 5].map((offset) => (parseInt(expanded.slice(offset, offset + 2), 16) || 0) / 255).join(' ');
  };
  const stream = ['q'];
  if (edit.styles?.backgroundColor && edit.styles.backgroundColor !== 'transparent') stream.push(`${rgb(edit.styles.backgroundColor)} rg 0 0 ${width} ${height} re f`);
  let top = 0;
  for (const line of lines) {
    const ascentFor = (run) => {
      const f = run.font.embedder?.font;
      return f?.unitsPerEm ? run.size * (f.ascent / f.unitsPerEm + (leading - (f.ascent - f.descent) / f.unitsPerEm) / 2) : run.size;
    };
    const baseline = edit.textLayoutVersion === 2 ? Math.max(...line.runs.map(ascentFor), line.size * 0.99) : line.size;
    const y = height - top - baseline;
    let x = edit.styles?.alignment === 'center' ? Math.max(0, (width - line.width) / 2) : edit.styles?.alignment === 'right' ? Math.max(0, width - line.width) : 0;
    for (const run of line.runs) {
      const color = rgb(run.styles.color);
      stream.push(`BT /${fonts.get(run.font)} ${run.size} Tf ${color} rg 1 0 0 1 ${x} ${y} Tm ${run.font.encodeText(run.text)} Tj ET`);
      for (const offset of [run.styles.underline ? -0.15 : null, run.styles.strikethrough ? 0.3 : null].filter((offset) => offset !== null)) {
        const ly = y + run.size * offset;
        stream.push(`${color} RG ${Math.max(.5, run.size * .05)} w ${x} ${ly} m ${x + run.width} ${ly} l S`);
      }
      x += run.width;
    }
    top += line.size * (edit.styles?.lineHeight || 1.3);
  }
  stream.push('Q');
  const resources = Object.fromEntries([...fonts].map(([font, name]) => [name, font.ref]));
  const xobjectRef = doc.context.register(doc.context.flateStream(stream.join('\n'), {
    Type: PDFName.of('XObject'), Subtype: PDFName.of('Form'), FormType: PDFNumber.of(1),
    BBox: doc.context.obj([0, 0, width, height]), Resources: doc.context.obj({ Font: resources }),
  }));
  return { xobjectRef, height, fontResourceName: fonts.values().next().value || 'F0' };
}

// WinAnsi covers U+0000–U+00FF minus a few + the CP1252 punctuation block.
// Cheap heuristic: anything with a code point > U+00FF is treated as
// non-WinAnsi so we embed Noto. Smart curly quotes (U+2018–U+201F) are caught
// by the same check since they sit above U+00FF — no separate range needed.
export function hasNonWinAnsi(text) {
  for (const ch of String(text)) {
    const cp = ch.codePointAt(0);
    if (cp > 0xff) return true;
  }
  return false;
}

/**
 * Resolve the font to render a value's appearance with. Returns the embedded
 * Helvetica for WinAnsi text; embeds + caches a Noto subset for the rest.
 * Caller passes a per-save cache object to avoid re-embedding Noto each field.
 *
 * @param {import('pdf-lib').PDFDocument} doc
 * @param {string} text
 * @param {{ helv: import('pdf-lib').PDFFont, noto?: import('pdf-lib').PDFFont, fontkit?: boolean }} cache
 * @returns {Promise<import('pdf-lib').PDFFont>}
 */
export async function resolveAppearanceFont(doc, text, cache) {
  if (!cache || !cache.helv) throw new Error('resolveAppearanceFont: fontCache.helv (an embedded Helvetica PDFFont) is required');
  if (!hasNonWinAnsi(text)) return cache.helv;
  if (cache.noto) return cache.noto;
  try {
    if (!cache.fontkit) {
      const fontkit = (await import('@pdf-lib/fontkit')).default;
      doc.registerFontkit(fontkit);
      cache.fontkit = true;
    }
    const url = '/fonts/NotoSans-Regular.ttf';
    const res = await fetch(url);
    const bytes = new Uint8Array(await res.arrayBuffer());
    cache.noto = await doc.embedFont(bytes, { subset: true });
    return cache.noto;
  } catch (e) {
    if (import.meta.env?.DEV) console.warn('Noto fallback failed; using Helvetica', e);
    return cache.helv; // WinAnsi substitution (? for missing glyphs)
  }
}

// ---------- Shared /AP helpers for non-text annotations ----------
//
// Markup, ink, shapes, checkmark/cross, image and signature annotations get a
// real /AP Form XObject just like text does (buildTextAppearance above), so
// /AP-honoring viewers paint OUR rendering verbatim instead of generating their
// own appearance. These are the shared low-level pieces: an ExtGState for
// constant alpha / blend mode, and a Form XObject wrapper around a content
// stream. Each appearance builder draws in box-local coords (BBox [0 0 w h],
// bottom-up); the annot's /Rect positions the box on the page.

let _gsCounter = 0;

/**
 * Register an ExtGState for fill (ca) + stroke (CA) alpha, optionally a blend
 * mode (e.g. 'Multiply' for highlights). Returns { name, ref } to wire into a
 * Form XObject's Resources and reference with `/<name> gs` in its stream.
 */
export function apGState(doc, { opacity = 1, blend } = {}) {
  const name = `GS${_gsCounter++}`;
  const entries = { Type: PDFName.of('ExtGState'), ca: PDFNumber.of(opacity), CA: PDFNumber.of(opacity) };
  if (blend) entries.BM = PDFName.of(blend);
  const ref = doc.context.register(doc.context.obj(entries));
  return { name, ref };
}

/**
 * Wrap a content-stream string in a Form XObject appearance and register it.
 * BBox is [0 0 width height]; Resources carries the ExtGState (and an optional
 * XObject map for image appearances). Returns the indirect PDFRef.
 */
export function apFormXObject(doc, { width, height, stream, gstate, xobjects }) {
  const resources = {};
  if (gstate) resources.ExtGState = doc.context.obj({ [gstate.name]: gstate.ref });
  if (xobjects) resources.XObject = doc.context.obj(xobjects);
  const xobjDict = {
    Type: PDFName.of('XObject'), Subtype: PDFName.of('Form'), FormType: PDFNumber.of(1),
    BBox: doc.context.obj([PDFNumber.of(0), PDFNumber.of(0), PDFNumber.of(width), PDFNumber.of(height)]),
    Resources: doc.context.obj(resources),
  };
  return doc.context.register(doc.context.flateStream(stream, xobjDict));
}

// Clamp a color component to [0,1] and trim to 3 decimals so stream operands
// stay valid (never 0-255 by mistake) and don't bloat with repeating decimals
// like 0.33333… (e.g. #555 → 0.333).
const fmtC = (n) => Math.max(0, Math.min(1, Number(n) || 0)).toFixed(3);

/**
 * Markup appearance in box-local coords (BBox [0 0 width height]).
 * - highlight: filled rect, Multiply blend so underlying text shows through.
 * - underline: stroked line near the bottom; strikethrough: at the middle.
 */
export function buildMarkupAppearance(doc, { type, width, height, color, opacity }) {
  const [r, g, b] = color.map(fmtC);
  let stream, gate;
  if (type === 'highlight') {
    gate = apGState(doc, { opacity, blend: 'Multiply' });
    stream = `/${gate.name} gs ${r} ${g} ${b} rg 0 0 ${width.toFixed(2)} ${height.toFixed(2)} re f`;
  } else {
    gate = apGState(doc, { opacity });
    const t = 1.5;                       // matches the legacy bake thickness
    const y = type === 'strikethrough' ? height / 2 : t / 2; // bottom for underline
    stream = `/${gate.name} gs ${r} ${g} ${b} RG ${t} w 0 ${y.toFixed(2)} m ${width.toFixed(2)} ${y.toFixed(2)} l S`;
  }
  return apFormXObject(doc, { width, height, stream, gstate: gate });
}

/**
 * Pen/ink appearance: stroke the polyline in box-local coords. Box origin is
 * the stroke's bottom-left; a point (top-left page coords) maps to
 * local x = p.x - minX, local y = maxY - p.y (flip within the box).
 * Round cap/join (1 J 1 j) matches the canvas pen.
 */
export function buildInkAppearance(doc, { points, minX, minY, maxX, maxY, color, opacity, strokeWidth }) {
  const width = maxX - minX, height = maxY - minY;
  const [r, g, b] = color.map(fmtC);
  const gate = apGState(doc, { opacity });
  const seg = points.map((p, i) => {
    const lx = (p.x - minX).toFixed(2), ly = (maxY - p.y).toFixed(2);
    return `${lx} ${ly} ${i === 0 ? 'm' : 'l'}`;
  }).join(' ');
  const stream = `/${gate.name} gs ${r} ${g} ${b} RG ${strokeWidth} w 1 J 1 j ${seg} S`;
  return apFormXObject(doc, { width, height, stream, gstate: gate });
}

const KAPPA = 0.5522847498;

/**
 * Vector shape appearance in box-local coords (BBox [0 0 width height], bottom-up).
 * rectangle/ellipse: inset by strokeWidth/2 so the stroke stays inside the BBox.
 * line/arrow: caller passes box-local, bottom-up endpoints (lx1,ly1,lx2,ly2).
 * checkmark/cross: reproduce the legacy fraction geometry.
 */
export function buildShapeAppearance(doc, { type, width, height, color, opacity, strokeWidth, lx1, ly1, lx2, ly2 }) {
  const [r, g, b] = color.map(fmtC);
  const sw = strokeWidth || 2;
  const gate = apGState(doc, { opacity });
  const stroke = `${r} ${g} ${b} RG ${sw} w 1 J 1 j`;
  let body;
  if (type === 'rectangle') {
    const i = sw / 2;
    body = `${(i).toFixed(2)} ${(i).toFixed(2)} ${(width - sw).toFixed(2)} ${(height - sw).toFixed(2)} re S`;
  } else if (type === 'ellipse') {
    const i = sw / 2, cx = width / 2, cy = height / 2, rx = width / 2 - i, ry = height / 2 - i;
    const ox = (rx * KAPPA).toFixed(2), oy = (ry * KAPPA).toFixed(2);
    const L = (cx - rx).toFixed(2), R = (cx + rx).toFixed(2), B = (cy - ry).toFixed(2), T = (cy + ry).toFixed(2);
    const CX = cx.toFixed(2), CY = cy.toFixed(2);
    body =
      `${CX} ${T} m ` +
      `${(cx + Number(ox)).toFixed(2)} ${T} ${R} ${(cy + Number(oy)).toFixed(2)} ${R} ${CY} c ` +
      `${R} ${(cy - Number(oy)).toFixed(2)} ${(cx + Number(ox)).toFixed(2)} ${B} ${CX} ${B} c ` +
      `${(cx - Number(ox)).toFixed(2)} ${B} ${L} ${(cy - Number(oy)).toFixed(2)} ${L} ${CY} c ` +
      `${L} ${(cy + Number(oy)).toFixed(2)} ${(cx - Number(ox)).toFixed(2)} ${T} ${CX} ${T} c S`;
  } else if (type === 'line' || type === 'arrow') {
    body = `${lx1.toFixed(2)} ${ly1.toFixed(2)} m ${lx2.toFixed(2)} ${ly2.toFixed(2)} l S`;
    if (type === 'arrow') {
      const dx = lx2 - lx1, dy = ly2 - ly1, ang = Math.atan2(dy, dx);
      const headLen = Math.min(15, Math.hypot(dx, dy) * 0.3);
      // Clamp the wings to the BBox — shallow-angle arrows can otherwise put a
      // wing a hair outside [0 0 width height], which strict /AP viewers may clip.
      const clampX = (v) => Math.max(0, Math.min(width, v));
      const clampY = (v) => Math.max(0, Math.min(height, v));
      const ax = clampX(lx2 - headLen * Math.cos(ang - Math.PI / 6)).toFixed(2);
      const ay = clampY(ly2 - headLen * Math.sin(ang - Math.PI / 6)).toFixed(2);
      const bx = clampX(lx2 - headLen * Math.cos(ang + Math.PI / 6)).toFixed(2);
      const by = clampY(ly2 - headLen * Math.sin(ang + Math.PI / 6)).toFixed(2);
      body += ` ${ax} ${ay} m ${lx2.toFixed(2)} ${ly2.toFixed(2)} l ${bx} ${by} l S`;
    }
  } else if (type === 'checkmark') {
    // legacy fractions, flipped to bottom-up: localY = height - topFraction*height
    const x1 = (width * 0.15).toFixed(2), y1 = (height * 0.45).toFixed(2);
    const x2 = (width * 0.4).toFixed(2),  y2 = (height * 0.2).toFixed(2);
    const x3 = (width * 0.85).toFixed(2), y3 = (height * 0.8).toFixed(2);
    body = `${x1} ${y1} m ${x2} ${y2} l ${x3} ${y3} l S`;
  } else if (type === 'cross') {
    const pad = Math.min(width, height) * 0.15;
    const a = pad.toFixed(2), wr = (width - pad).toFixed(2), hr = (height - pad).toFixed(2);
    body = `${a} ${hr} m ${wr} ${a} l S ${wr} ${hr} m ${a} ${a} l S`;
  } else {
    body = '';
  }
  return apFormXObject(doc, { width, height, stream: `/${gate.name} gs ${stroke} ${body}`, gstate: gate });
}

/**
 * Image/signature appearance: draw an embedded image XObject object-contain
 * inside the box (BBox [0 0 boxW boxH]). `image` is a pdf-lib PDFImage.
 */
export function buildImageAppearance(doc, { image, boxW, boxH }) {
  const imgW = image.width || boxW, imgH = image.height || boxH;
  // Guard degenerate dimensions: a zero box/image would make scale NaN and emit
  // an invalid `NaN 0 0 NaN … cm`. Throwing lets buildStamp fall back to the
  // placeholder (the bake covers it) instead of writing a blank /AP.
  if (!(boxW > 0) || !(boxH > 0) || !(imgW > 0) || !(imgH > 0)) {
    throw new Error('buildImageAppearance: non-positive dimensions');
  }
  const scale = Math.min(boxW / imgW, boxH / imgH);
  const drawW = imgW * scale, drawH = imgH * scale;
  const ox = (boxW - drawW) / 2, oy = (boxH - drawH) / 2;
  const name = 'Img';
  const stream = `q ${drawW.toFixed(2)} 0 0 ${drawH.toFixed(2)} ${ox.toFixed(2)} ${oy.toFixed(2)} cm /${name} Do Q`;
  return apFormXObject(doc, { width: boxW, height: boxH, stream, xobjects: { [name]: image.ref } });
}
